"use client";

import React from "react";
import { PhoneCall, CalendarCheck, Clock, ShieldAlert, ArrowRight, Zap } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencycalltoaction" data-nav="light" className="relative bg-background py-16 md:py-20 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative rounded-2xl bg-primary text-primary-foreground p-8 md:p-12 lg:p-16 shadow-2xl border border-primary/20 overflow-hidden">
          {/* Subtle background decoration elements */}
          <div aria-hidden="true" className="absolute -right-20 -top-20 size-80 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          <div aria-hidden="true" className="absolute -left-20 -bottom-20 size-72 rounded-full bg-primary-foreground/5 blur-2xl pointer-events-none" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 flex items-center gap-1.5">
                  <Clock className="size-3.5" />
                  <span>{"24/7 Rapid Response"}</span>
                </Badge>
                <Badge variant="outline" className="text-primary-foreground border-primary-foreground/30 bg-primary-foreground/10 px-3 py-1 flex items-center gap-1.5">
                  <ShieldAlert className="size-3.5" />
                  <span>{"Jonesboro, GA Local Plumbers"}</span>
                </Badge>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight leading-tight text-primary-foreground">
                {"Plumbing Disaster? We Are Ready Right Now."}
              </h2>

              <p className="text-base sm:text-lg text-primary-foreground/90 max-w-xl font-default">
                {"Burst pipes, sewage backups, or sudden water heater leaks. Speak with a live local plumber immediately and get a technician dispatched to your door."}
              </p>

              {/* Trust Indicators */}
              <div className="pt-2 flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-primary-foreground/80">
                <div className="flex items-center gap-2">
                  <Zap className="size-4 text-accent shrink-0" />
                  <span>{"30–60 Min Average Arrival"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="size-4 text-accent shrink-0" />
                  <span>{"Upfront, Honest Pricing"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="size-4 text-accent shrink-0" />
                  <span>{"Licensed & Insured"}</span>
                </div>
              </div>
            </div>

            {/* Right Action Column */}
            <div className="lg:col-span-5 flex flex-col gap-4 bg-background/10 backdrop-blur-xs p-6 sm:p-8 rounded-xl border border-primary-foreground/15">
              <div className="text-center sm:text-left space-y-1">
                <span className="text-xs font-semibold tracking-wider uppercase text-accent">
                  {"Immediate Assistance"}
                </span>
                <p className="text-sm text-primary-foreground/85">
                  {"Call directly for immediate emergency dispatch:"}
                </p>
              </div>

              {/* Direct Phone Call Button */}
              <a href="tel:4708479076" className="w-full inline-block group" aria-label="Call Jonesboro Emergency Plumber now at (470) 847-9076">
                <div className="w-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all duration-200 rounded-lg p-4 flex items-center justify-between shadow-md group-hover:shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="size-11 rounded-full bg-accent-foreground/10 flex items-center justify-center shrink-0">
                      <PhoneCall className="size-5 text-accent-foreground animate-pulse" />
                    </div>
                    <div className="text-left">
                      <div className="text-xs uppercase font-bold tracking-wide text-accent-foreground/80">
                        {"Tap to Call 24/7"}
                      </div>
                      <div className="text-xl sm:text-2xl font-bold font-default tracking-tight text-accent-foreground">
                        {"(470) 847-9076"}
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="size-5 text-accent-foreground transition-transform group-hover:translate-x-1" />
                </div>
              </a>

              <div className="relative flex items-center justify-center my-1">
                <div className="border-t border-primary-foreground/20 w-full" />
                <span className="bg-primary px-3 text-xs uppercase tracking-widest text-primary-foreground/70 font-semibold absolute">
                  {"or"}
                </span>
              </div>

              {/* Request Service Form Link Button */}
              <Button asChild size="lg" variant="secondary" className="w-full h-12 text-base font-semibold text-secondary-foreground shadow-sm hover:bg-secondary/90 transition-colors">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <CalendarCheck className="size-5" />
                  <span>{"Request Service Online"}</span>
                </Link>
              </Button>

              <p className="text-xs text-center text-primary-foreground/70 pt-1">
                {"Non-urgent requests typically receive replies within 15 minutes."}
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}